import { resolveBuiltinAgentControllerConversationCapability } from "./agent-controller-conversation-capability.js";
import * as z$1 from "zod/v4";
//#region src/schemas/daemon/agent-controller-ref.ts
const BUILTIN_AGENT_CONTROLLER_REF_PREFIX = "builtin:";
const AGENT_CONTROLLER_REF_SEPARATOR = ":";
const HOSTED_CENTER_AGENT_CONTROLLER_REF = "hosted:center-agent";
const HOSTED_CENTER_AGENT_CONTROLLER_REF_ID = "center-agent";
const HOSTED_REMOTE_AGENT_CONTROLLER_REF = "hosted:remote-agent";
const agentControllerRefSchema = z$1.string().transform((value, context) => {
	const parsed = parseAgentControllerRef(value);
	if (!parsed) {
		context.addIssue({
			code: "custom",
			message: "Agent Controller ref is invalid"
		});
		return z$1.NEVER;
	}
	return parsed.ref;
});
function normalizeOptionalText(value) {
	if (typeof value !== "string") return null;
	const normalized = value.trim();
	return normalized.length > 0 ? normalized : null;
}
function normalizeBuiltinControllerId(agentControllerRef) {
	const normalized = normalizeOptionalText(agentControllerRef);
	if (!normalized) return null;
	if (normalized.includes(":") && !normalized.startsWith(BUILTIN_AGENT_CONTROLLER_REF_PREFIX)) return null;
	const builtinId = normalized.startsWith(BUILTIN_AGENT_CONTROLLER_REF_PREFIX) ? normalized.slice(8) : normalized;
	if (builtinId.length === 0 || builtinId.includes(":")) return null;
	return builtinId;
}
function normalizeKnownBuiltinAgentControllerId(agentControllerRef) {
	const builtinId = normalizeBuiltinControllerId(agentControllerRef);
	return builtinId && resolveBuiltinAgentControllerConversationCapability(builtinId) ? builtinId : null;
}
function normalizeBuiltinAgentControllerId(agentControllerRef) {
	return normalizeBuiltinControllerId(agentControllerRef);
}
function isExplicitBuiltinAgentControllerRef(agentControllerRef) {
	const normalized = normalizeOptionalText(agentControllerRef);
	return normalized?.startsWith(BUILTIN_AGENT_CONTROLLER_REF_PREFIX) === true && normalizeKnownBuiltinAgentControllerId(normalized) !== null;
}
function parseAgentControllerRef(agentControllerRef) {
	const normalized = normalizeOptionalText(agentControllerRef);
	if (!normalized) return null;
	if (normalized === "hosted:center-agent") return {
		kind: HOSTED_CENTER_AGENT_CONTROLLER_REF,
		id: HOSTED_CENTER_AGENT_CONTROLLER_REF_ID,
		ref: HOSTED_CENTER_AGENT_CONTROLLER_REF
	};
	if (normalized === "hosted:remote-agent") return {
		kind: HOSTED_REMOTE_AGENT_CONTROLLER_REF,
		id: "remote-agent",
		ref: normalized
	};
	for (const kind of ["builtin", "custom"]) {
		const prefix = `${kind}${AGENT_CONTROLLER_REF_SEPARATOR}`;
		if (!normalized.startsWith(prefix)) continue;
		const id = normalized.slice(prefix.length).trim();
		if (id.length === 0 || id.includes(AGENT_CONTROLLER_REF_SEPARATOR)) return null;
		return {
			kind,
			id,
			ref: `${kind}${AGENT_CONTROLLER_REF_SEPARATOR}${id}`
		};
	}
	return null;
}
function canonicalizeAgentControllerRef(agentControllerRef) {
	return parseAgentControllerRef(agentControllerRef)?.ref ?? null;
}
function buildAgentControllerRoutingKey(agentControllerRef) {
	return canonicalizeAgentControllerRef(agentControllerRef);
}
function canonicalizeBuiltinAgentControllerRef(agentControllerRef) {
	const builtinId = normalizeBuiltinControllerId(agentControllerRef);
	return builtinId ? `${BUILTIN_AGENT_CONTROLLER_REF_PREFIX}${builtinId}` : null;
}
function areBuiltinAgentControllerRefsEquivalent(left, right) {
	const parsedLeft = parseAgentControllerRef(left);
	const parsedRight = parseAgentControllerRef(right);
	if (!(parsedLeft?.kind === "builtin" && parsedRight?.kind === "builtin")) return false;
	return parsedLeft.ref === parsedRight.ref;
}
function isHostedCenterAgentControllerRef(agentControllerRef) {
	return parseAgentControllerRef(agentControllerRef)?.kind === HOSTED_CENTER_AGENT_CONTROLLER_REF;
}
function isHostedAgentControllerRef(value) {
	const ref = parseAgentControllerRef(value)?.ref;
	return ref === "hosted:center-agent" || ref === "hosted:remote-agent";
}
//#endregion
export { HOSTED_CENTER_AGENT_CONTROLLER_REF, HOSTED_CENTER_AGENT_CONTROLLER_REF_ID, HOSTED_REMOTE_AGENT_CONTROLLER_REF, agentControllerRefSchema, areBuiltinAgentControllerRefsEquivalent, buildAgentControllerRoutingKey, canonicalizeAgentControllerRef, canonicalizeBuiltinAgentControllerRef, isExplicitBuiltinAgentControllerRef, isHostedAgentControllerRef, isHostedCenterAgentControllerRef, normalizeBuiltinAgentControllerId, normalizeKnownBuiltinAgentControllerId, parseAgentControllerRef };

//# sourceMappingURL=agent-controller-ref.js.map